
import React, { useState, useMemo } from 'react';
import { Produto, FichaTecnica, Insumo, FichaTecnicaItem } from '../../types';
import { useFichaTecnica } from '../../hooks/useFichaTecnica';
import { useInsumos } from '../../hooks/useInsumos';
import { formatCurrency } from '../../constants';

interface AdminFichaTecnicaProps {
  empresaId: string;
  produtos: Produto[];
  selectedProdutoId: string | null;
  setSelectedProdutoId: (id: string | null) => void;
  isDemo?: boolean;
}

export const AdminFichaTecnica: React.FC<AdminFichaTecnicaProps> = ({ 
  empresaId, 
  produtos, 
  selectedProdutoId, 
  setSelectedProdutoId, 
  isDemo 
}) => {
  const { ficha, loading, error, saveFicha, addItem, removeItem, calculateCost } = useFichaTecnica(selectedProdutoId || undefined);
  const { insumos } = useInsumos(empresaId);

  const selectedProduto = useMemo(() => produtos.find(p => p.id === selectedProdutoId), [produtos, selectedProdutoId]);

  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItem, setNewItem] = useState<Partial<FichaTecnicaItem>>({ insumo_id: '', quantidade: 0 });

  const handleRecalcular = async () => {
    if (isDemo) return alert("Modo Demo: Ação não permitida.");
    try {
      await calculateCost();
      alert("Custo e CMV recalculados com sucesso!");
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleSaveBase = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isDemo) return alert("Modo Demo: Ação não permitida.");
    const formData = new FormData(e.currentTarget);
    const data = {
      rendimento: Number(formData.get('rendimento')),
      tempo_preparo_minutos: Number(formData.get('tempo_preparo_minutos')),
      perda_percentual: Number(formData.get('perda_percentual')),
      observacoes: formData.get('observacoes') as string
    };
    try {
      await saveFicha(data);
      alert("Dados da ficha salvos!");
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ficha) {
      // Se não houver ficha, cria uma básica primeiro
      try {
        const novaFicha = await saveFicha({ rendimento: 1 });
        await addItem({ ...newItem, ficha_tecnica_id: novaFicha.id });
      } catch (err: any) {
        alert(err.message);
        return;
      }
    } else {
      try {
        await addItem(newItem);
      } catch (err: any) {
        alert(err.message);
        return;
      }
    }
    setIsAddingItem(false);
    setNewItem({ insumo_id: '', quantidade: 0 });
  };

  if (!selectedProdutoId) {
    return (
      <div className="p-4 md:p-10 animate-fade-in space-y-6 max-w-7xl mx-auto">
        <div className="bg-slate-900 p-8 md:p-12 rounded-[3rem] shadow-2xl">
          <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight mb-2">Engenharia de Cardápio</h3>
          <p className="text-slate-400 font-bold text-sm md:text-base">Selecione um produto para gerenciar sua ficha técnica e CMV.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {produtos.map(p => (
            <button 
              key={p.id} 
              onClick={() => setSelectedProdutoId(p.id)}
              className="bg-white border border-slate-200 p-6 rounded-[2rem] text-left hover:border-orange-600 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center font-black text-slate-300 overflow-hidden shrink-0">
                  {p.imagem_url ? <img src={p.imagem_url} className="w-full h-full object-cover" /> : p.nome.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-black text-slate-900 truncate group-hover:text-orange-600 transition-colors uppercase tracking-tight">{p.nome}</h4>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{p.categoria}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-10 animate-fade-in space-y-6 max-w-7xl mx-auto">
      <button 
        onClick={() => setSelectedProdutoId(null)}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-900 font-black text-xs uppercase tracking-widest transition-colors mb-4"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7"></path></svg>
        Voltar para Lista
      </button>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Lado Esquerdo - Dados e Itens */}
        <div className="flex-1 space-y-6">
          <section className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
            <h4 className="text-xl font-black text-slate-900 mb-6 tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center text-sm">A</span>
              Ficha Técnica: {selectedProduto?.nome}
            </h4>
            
            <form onSubmit={handleSaveBase} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Rendimento (Porções/Un)</label>
                <input name="rendimento" type="number" step="0.01" defaultValue={ficha?.rendimento || 1} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Tempo Preparo (Min)</label>
                <input name="tempo_preparo_minutos" type="number" defaultValue={ficha?.tempo_preparo_minutos || 0} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Fator de Perda (%)</label>
                <input name="perda_percentual" type="number" step="0.01" defaultValue={ficha?.perda_percentual || 0} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Observações</label>
                <textarea name="observacoes" defaultValue={ficha?.observacoes || ''} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all h-24" />
              </div>
              <div className="md:col-span-2">
                <button type="submit" className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-orange-600 transition-all shadow-lg">Salvar Dados Base</button>
              </div>
            </form>
          </section>

          <section className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                <span className="w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center text-sm">B</span>
                Ingredientes
              </h4>
              <button 
                onClick={() => setIsAddingItem(true)}
                className="bg-orange-600/10 text-orange-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 hover:text-white transition-all"
              >
                + Adicionar Item
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Insumo</th>
                    <th className="text-center py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Qtd</th>
                    <th className="text-right py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Custo</th>
                    <th className="text-right py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Subtotal</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {ficha?.itens?.map(item => (
                    <tr key={item.id} className="group">
                      <td className="py-4">
                        <span className="font-bold text-slate-900">{item.insumo?.nome}</span>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">{item.insumo?.unidade_medida?.sigla}</p>
                      </td>
                      <td className="py-4 text-center font-bold text-slate-600">{item.quantidade}</td>
                      <td className="py-4 text-right font-bold text-slate-600">{formatCurrency(item.custo_momento)}</td>
                      <td className="py-4 text-right font-black text-slate-900">{formatCurrency(item.quantidade * item.custo_momento)}</td>
                      <td className="py-4 text-right">
                        <button onClick={() => removeItem(item.id)} className="p-2 text-slate-300 hover:text-red-600 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(!ficha?.itens || ficha.itens.length === 0) && (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-slate-400 font-bold italic text-sm">
                        Nenhum ingrediente adicionado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Lado Direito - Resumo e Fixos */}
        <div className="lg:w-80 space-y-6">
          <section className="bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl text-white sticky top-10">
            <h4 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-6 flex items-center gap-3">
              <span className="w-6 h-6 bg-white/10 rounded flex items-center justify-center text-[10px]">C</span>
              Resumo Financeiro
            </h4>
            
            <div className="space-y-6">
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Custo Produção Tot.</p>
                <p className="text-3xl font-black tracking-tighter">{formatCurrency(ficha?.custo_total || 0)}</p>
              </div>
              
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">CMV Sugerido</p>
                <p className="text-3xl font-black tracking-tighter text-orange-500">{(ficha?.cmv_total || 0).toFixed(2)}%</p>
                <p className="text-[10px] font-bold text-slate-500 mt-1 italic">* Baseado no preço de venda atual de {formatCurrency(selectedProduto?.preco || 0)}</p>
              </div>

              <div className="pt-6 border-t border-white/5 space-y-4">
                <button 
                  onClick={handleRecalcular}
                  disabled={loading || !ficha?.id}
                  className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-3 ${loading ? 'bg-slate-800 text-slate-500' : 'bg-white text-slate-900 hover:bg-orange-600 hover:text-white'}`}
                >
                   {loading ? <div className="w-4 h-4 border-2 border-slate-500 border-t-white rounded-full animate-spin"></div> : (
                     <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                        Recalcular CMV
                     </>
                   )}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {isAddingItem && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 shadow-2xl animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Adicionar Ingrediente</h3>
              <button onClick={() => setIsAddingItem(false)} className="text-slate-400 hover:text-slate-900">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <form onSubmit={handleAddItem} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Insumo</label>
                <select 
                  required 
                  value={newItem.insumo_id} 
                  onChange={e => setNewItem({...newItem, insumo_id: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all cursor-pointer"
                >
                  <option value="">Selecione...</option>
                  {insumos.map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Quantidade</label>
                <input 
                  required 
                  type="number" 
                  step="0.01" 
                  value={newItem.quantidade} 
                  onChange={e => setNewItem({...newItem, quantidade: Number(e.target.value)})}
                  className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" 
                />
              </div>

              <button type="submit" className="w-full bg-slate-900 text-white font-black py-5 rounded-2xl hover:bg-orange-600 transition-all shadow-xl">
                Adicionar à Ficha
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
